import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Landing from "@/pages/Landing";
import BlogPost from "@/pages/BlogPost";
import Resources from "@/pages/Resources";
import About from "@/pages/About";
import Legal from "@/pages/Legal";
import SeoLanding from "@/pages/SeoLanding";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import ContentManager from "@/pages/admin/ContentManager";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { PersonaProvider } from "@/contexts/PersonaContext";
import { SiteContentProvider } from "@/contexts/SiteContentContext";
import { Toaster } from "@/components/ui/sonner";
import ScrollManager from "@/components/ScrollManager";
import AnalyticsManager from "@/components/AnalyticsManager";
import CookieConsent from "@/components/CookieConsent";
import BookingRouter from "@/components/BookingRouter";
import RoutePage from "@/pages/RoutePage";
import NotFound from "@/pages/NotFound";
// Dev/preview-only prototype, code-split so it stays out of the public bundle.
// The component itself redirects to Home when NODE_ENV === "production".
const BookingPrototype = lazy(() => import("@/pages/BookingPrototype"));

import { SpainMoveAssistant } from "@/components/SpainMoveAssistant";
import { BookingProvider } from "@/contexts/BookingContext";

// Spain-only public SEO landings (PT/IT slugs removed from routing; backend returns 404 for inactive destinations)
const SEO_SLUGS = ["retire-to-spain", "move-to-spain", "digital-nomad-spain"];

// Legacy URL redirects (only where an equivalent page exists)
const REDIRECTS = {
  "/atlas": "/resources",
  "/blog": "/resources",
  "/retire": "/spain/retire/",
  "/relocate": "/spain/moving-to-spain/",
  "/move-to-spain": "/spain/moving-to-spain/",
  "/retire-to-spain": "/spain/retire/",
  "/digital-nomad-spain": "/blog/spain-digital-nomad-visa-for-americans",
  "/beckham-law-american-expats-spain": "/blog/us-taxes-living-in-spain",
  "/beckham-law-spain-american-expats": "/blog/us-taxes-living-in-spain",
  "/nie-number-americans-spain-2": "/nie-number-americans-spain",
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AdminAuthProvider>
          <PersonaProvider>
            <SiteContentProvider>
              <BookingProvider>
              <BrowserRouter>
                <ScrollManager />
                <AnalyticsManager />
                <CookieConsent />
                <BookingRouter />
                <SpainMoveAssistant>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  {Object.entries(REDIRECTS).map(([from, to]) => (
                    <Route key={from} path={from} element={<Navigate to={to} replace />} />
                  ))}
                  <Route path="/about" element={<About />} />
                  <Route path="/resources" element={<Resources />} />
                  <Route path="/privacy" element={<Legal />} />
                  <Route path="/terms" element={<Legal />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/:country/:route" element={<RoutePage />} />
                  <Route path="/:country/:route/*" element={<NotFound />} />
                  {SEO_SLUGS.map((s) => (
                    <Route key={s} path={`/${s}`} element={<SeoLanding slugProp={s} />} />
                  ))}
                  <Route path="/admin" element={<AdminLogin />} />
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/content" element={<ContentManager />} />
                  {/* Isolated Free Call booking prototype (Option B). Dev/preview only:
                      the page redirects to Home in production builds. Not linked from any public CTA. */}
                  <Route path="/booking-prototype" element={<Suspense fallback={null}><BookingPrototype /></Suspense>} />
                  {/* Catch-all for AI-generated SEO pages (must be last) */}
                  <Route path="/:slug" element={<SeoLanding />} />
                </Routes>
                </SpainMoveAssistant>
              </BrowserRouter>
              </BookingProvider>
            </SiteContentProvider>
          </PersonaProvider>
        </AdminAuthProvider>
      </HelmetProvider>
      <Toaster position="bottom-right" theme="light" />
    </div>
  );
}

export default App;
